import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Button } from "@/components/ui/button";
import { formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import {
  companyBiometricDeviceDisplayName,
  deleteCompanyBiometricDevice,
  fetchCompanyBiometricDeviceById,
  getCompanyBiometricDeviceById,
} from "@/lib/admin/companyBiometricDevicesStore";
import { companyBiometricDeviceFormErrorMessage } from "@/lib/admin/companyBiometricDeviceForm";
import { formatBiometricDeviceProtocol } from "@/lib/admin/biometricDeviceForm";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  Building2,
  Calendar,
  Cpu,
  Edit,
  Fingerprint,
  Loader2,
  MapPin,
  Network,
  Server,
  Trash2,
} from "lucide-react";
import { isAxiosError } from "axios";
import { format, isValid, parseISO } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  const display = value.trim() || "—";
  return (
    <div className="flex gap-3 py-3 border-b border-border/40 last:border-0">
      <div className="shrink-0 mt-0.5 text-muted-foreground">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-sm text-foreground mt-1 break-words">{display}</p>
      </div>
    </div>
  );
}

export default function CompanyBiometricDeviceView() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "company_biometric_device_can_view");
  const canUpdate = can(user, "company_biometric_device_can_update");
  const canDelete = can(user, "company_biometric_device_can_delete");

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let mounted = true;
    void (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        await fetchCompanyBiometricDeviceById(id);
        if (mounted) refresh();
      } catch (e) {
        console.error("[CompanyBiometricDeviceView] Load failed", e);
        if (mounted) {
          if (isAxiosError(e) && e.response?.status === 404) setNotFound(true);
          else toast.error(companyBiometricDeviceFormErrorMessage(e));
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, refresh]);

  const device = useMemo(() => (id ? getCompanyBiometricDeviceById(id) : undefined), [id, version]);

  const runDelete = async () => {
    if (!device || isDeleting) return;
    setIsDeleting(true);
    try {
      const r = await deleteCompanyBiometricDevice(device.id);
      toast.success(r.message);
      setConfirmDelete(false);
      navigate("/company-biometric-devices", { replace: true });
    } catch (e) {
      console.error("[CompanyBiometricDeviceView] Delete failed", e);
      toast.error(companyBiometricDeviceFormErrorMessage(e));
    } finally {
      setIsDeleting(false);
    }
  };

  if (!canView) {
    return (
      <PermissionDenied
        title="Company biometric device access required"
        message="You don’t have permission to view company biometric devices. Ask an administrator to grant company_biometric_device_can_view."
      />
    );
  }

  if (!id) {
    return (
      <Layout title="Company biometric device">
        <div className="glass-card p-8 text-sm text-muted-foreground">Missing device id.</div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout title="Company biometric device">
        <div className="glass-card p-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin shrink-0" />
          Loading device…
        </div>
      </Layout>
    );
  }

  if (notFound || !device) {
    return (
      <Layout title="Company biometric device">
        <div className="space-y-4">
          <div className="glass-card p-8 text-sm text-muted-foreground">Company biometric device not found.</div>
          <Button variant="glass" className="gap-2" asChild>
            <Link to="/company-biometric-devices">
              <ArrowLeft className="w-4 h-4" />
              Back to devices
            </Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const title = companyBiometricDeviceDisplayName(device);

  return (
    <Layout title={title}>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <Link to="/company-biometric-devices">
              <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div className="min-w-0">
              <h1 className="text-2xl font-display font-bold text-foreground truncate">{title}</h1>
              <p className="text-sm text-muted-foreground mt-1 font-mono truncate">
                {device.ipAddress}:{device.port}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canUpdate ? (
              <Link to={`/company-biometric-devices/${device.id}/edit`}>
                <Button variant="glass" className="gap-2">
                  <Edit className="h-4 w-4" />
                  Edit
                </Button>
              </Link>
            ) : null}
            {canDelete ? (
              <Button variant="ghost" className="gap-2 text-destructive hover:bg-destructive/10" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            ) : null}
          </div>
        </div>

        <div className="glass-card p-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10">
              <Fingerprint className="h-7 w-7 text-primary" />
            </div>
            <span
              className={cn(
                "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border",
                device.isActive
                  ? "bg-emerald-500/15 text-emerald-800 dark:text-emerald-400 border-emerald-500/25"
                  : "bg-muted/50 text-muted-foreground border-border/60",
              )}
            >
              {device.isActive ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="glass-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Server className="w-4 h-4 text-primary shrink-0" />
              <h3 className="font-display font-semibold text-foreground text-sm">Connection</h3>
            </div>
            <InfoRow icon={Server} label="IP address" value={device.ipAddress} />
            <InfoRow icon={Network} label="Port" value={String(device.port)} />
            <InfoRow icon={MapPin} label="Location" value={device.location ?? ""} />
            <InfoRow
              icon={Calendar}
              label="Last connected"
              value={
                device.lastConnectedAt && isValid(parseISO(device.lastConnectedAt))
                  ? format(parseISO(device.lastConnectedAt), "PPpp")
                  : device.lastConnectedAt ?? ""
              }
            />
            <InfoRow
              icon={Calendar}
              label="Last attendance sync"
              value={
                device.attendanceLastSyncedAt && isValid(parseISO(device.attendanceLastSyncedAt))
                  ? format(parseISO(device.attendanceLastSyncedAt), "PPpp")
                  : device.attendanceLastSyncedAt ?? ""
              }
            />
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-4 h-4 text-primary shrink-0" />
              <h3 className="font-display font-semibold text-foreground text-sm">Assignment</h3>
            </div>
            <InfoRow icon={Building2} label="Company" value={device.companyName ?? `ID ${device.companyId}`} />
            <InfoRow icon={MapPin} label="Warehouse" value={device.warehouseName ?? ""} />
            <InfoRow icon={Cpu} label="Catalog model" value={device.biometricModel ?? ""} />
            <InfoRow icon={Cpu} label="Driver" value={device.biometricDriver ?? ""} />
            <InfoRow
              icon={Network}
              label="Protocol"
              value={device.biometricProtocol ? formatBiometricDeviceProtocol(device.biometricProtocol) : ""}
            />
            <InfoRow icon={Fingerprint} label="Serial number" value={device.serialNumber ?? ""} />
          </div>
        </div>

        <div className="glass-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-4 h-4 text-primary shrink-0" />
            <h3 className="font-display font-semibold text-foreground text-sm">Record</h3>
          </div>
          <InfoRow
            icon={Calendar}
            label="Created"
            value={isValid(parseISO(device.createdAt)) ? format(parseISO(device.createdAt), "PP") : device.createdAt}
          />
          <InfoRow icon={Calendar} label="Last updated" value={formatCatalogAuditDate(device.updatedAt)} />
        </div>

        <ConfirmAlertDialog
          open={confirmDelete}
          onOpenChange={(o) => !o && setConfirmDelete(false)}
          title="Delete this company device?"
          description={
            <>
              Are you sure you want to delete <strong className="text-foreground font-semibold">{title}</strong>? This cannot be undone.
            </>
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void runDelete()}
        />
      </div>
    </Layout>
  );
}
